// BUG_REPORTER — TEMPORARY FEATURE. Delete this file per docs/BUG_REPORTER.md.
//
// Typed client for /api/bug-reports. Lives in lib/ like every other API module —
// API calls never live inside a feature folder.

import { get, postJson, postFormData, patch, deleteRequest, getApiBaseUrl } from './apiClient';

export type BugStatus = 'open' | 'triaged' | 'in_progress' | 'fixed' | 'wont_fix';
export type BugSeverity = 'blocker' | 'major' | 'minor' | 'cosmetic';

export type BugReportSummary = {
  id: string;
  ticketNumber: string;
  title: string;
  description: string;
  severity: BugSeverity;
  status: BugStatus;
  statusChangedAt: string | null;
  createdAt: string;
  pageLabel: string;
  path: string;
  hasScreenshot: boolean;
  reporterName: string;
  isMine: boolean;
  /** BR-1: the comments written in USC, oldest first. Never the ClickUp ticket's. */
  discussion: BugDiscussionComment[];
  /** BR-1: every real status transition, oldest first. */
  statusHistory: BugStatusChange[];
  /** D47: the earlier report this one is another instance of, or null. */
  linkedTo: BugReportLink | null;
  /** D47: the reports linked to this one, oldest first — those you may see. */
  instances: BugReportInstance[];
  /** D47: every link and unlink, oldest first. */
  linkHistory: BugLinkEvent[];
};

/**
 * D47: `same` — the original was open when linked, so this report shows the
 * original's status while linked. `came_back` — the original was already Fixed or
 * Won't fix, so this one keeps its own status and reads "Came back after …".
 */
export type BugLinkKind = 'same' | 'came_back';

/** D47: the original, always one level up. `title` and `status` are null when it is not yours to see. */
export type BugReportLink = {
  id: string;
  ticketNumber: string;
  kind: BugLinkKind;
  at: string | null;
  byName: string;
  title: string | null;
  status: BugStatus | null;
};

export type BugReportInstance = {
  id: string;
  ticketNumber: string;
  title: string;
  status: BugStatus;
  createdAt: string;
  kind: BugLinkKind | null;
  /** When it was linked here, and by whom. */
  linkedAt: string | null;
  linkedByName: string;
};

export type BugLinkEvent = {
  action: 'linked' | 'unlinked';
  ticketNumber: string;
  kind: BugLinkKind | null;
  /** Set when it moved because the report it was linked to was itself linked elsewhere. */
  movedWith: string;
  byName: string;
  at: string;
};

/**
 * BR-1: one comment in a report's discussion — written in USC and stored only in
 * USC, never sent to ClickUp (O-27, PROVISIONAL). Plain text: render `body` as
 * text, never as HTML or Markdown.
 */
export type BugDiscussionComment = {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
  editedAt: string | null;
  /** Only your own comment may be edited or deleted. */
  isMine: boolean;
};

/** `via`: the original it was following when it moved (D47). */
export type BugStatusChange = { from: BugStatus; to: BugStatus; at: string; via?: string };

/** The server refuses longer (BUG_COMMENT_TOO_LONG); the box stops at the same length. */
export const COMMENT_MAX_CHARS = 2000;

export type BugReportContext = {
  submissionId: string;
  title: string;
  description: string;
  severity: BugSeverity;
  path: string;
  search: string;
  pageLabel: string;
  entityType: string;
  entityId: string;
  userAgent: string;
  viewport: string;
  /** D47: "Same problem as an earlier report?" — that report's id, or '' for a new bug. */
  sameAsId: string;
};

/** What a reporter sees on the badge. Deliberately plain English, no jargon. */
export const BUG_STATUS_LABEL: Record<BugStatus, string> = {
  open: 'Reported',
  triaged: 'Investigating',
  in_progress: 'Being fixed',
  fixed: 'Fixed',
  wont_fix: "Won't fix",
};

export const SEVERITY_LABEL: Record<BugSeverity, string> = {
  blocker: "Blocking — I can't work",
  major: 'Major — wrong or missing data',
  minor: 'Minor — annoying but workable',
  cosmetic: 'Cosmetic — looks wrong',
};

/**
 * File a report. The screenshot is optional: a capture failure must never stop
 * someone telling us about a bug.
 */
export async function submitBugReport(context: BugReportContext, screenshot: Blob | null) {
  const fd = new FormData();
  (Object.keys(context) as Array<keyof BugReportContext>).forEach((key) => {
    fd.append(key, String(context[key] ?? ''));
  });
  if (screenshot) fd.append('screenshot', screenshot, `${context.submissionId}.png`);

  return postFormData<{ success: boolean; message: string; data: { report: BugReportSummary; duplicate: boolean } }>(
    '/bug-reports',
    fd
  );
}

/**
 * The three lists the page offers (owner's decision 2026-09-14,
 * docs/plans/DECISIONS.md D37). `open` is every status still being worked —
 * Reported, Investigating and Being fixed together.
 */
export type BugReportView = 'open' | 'fixed' | 'all';

export type BugReportCounts = Record<BugReportView, number>;

export type BugReportListParams = {
  page?: number;
  limit?: number;
  view?: BugReportView;
  /** Narrow to the caller's own reports. */
  mine?: boolean;
  /** A title or ticket number, matched as literal text. */
  q?: string;
};

export async function listBugReports(params: BugReportListParams = {}) {
  const { page = 1, limit = 25, view = 'all', mine = false, q = '' } = params;
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (view !== 'all') qs.set('view', view);
  if (mine) qs.set('mine', 'true');
  if (q.trim()) qs.set('q', q.trim());

  return get<{
    success: boolean;
    data: {
      reports: BugReportSummary[];
      counts: BugReportCounts;
      pagination: { page: number; limit: number; total: number; pages: number };
    };
  }>(`/bug-reports?${qs.toString()}`);
}

/**
 * One report, as the caller is allowed to see it. The target of the link a
 * ClickUp ticket carries (`/bug-reports?report=<id>`), since the report may sit
 * on any page of the list, or be filtered out of it.
 */
export async function getBugReport(id: string) {
  return get<{ success: boolean; data: { report: BugReportSummary } }>(
    `/bug-reports/${encodeURIComponent(id)}`
  );
}

/** One reply in a comment's thread. */
export type BugReportReply = {
  id: string;
  author: string;
  /** ISO time, or null when ClickUp sent none. */
  createdAt: string | null;
  text: string;
};

/** One comment on the report's ClickUp ticket, with its thread's replies. */
export type BugReportComment = BugReportReply & { replies: BugReportReply[] };

/**
 * The comments on a report's ClickUp ticket, oldest first — only the ones a
 * developer started with `[client-facing]`, the marker taken off; the server
 * keeps every other one back (owner's decisions 2026-09-14,
 * docs/plans/DECISIONS.md D37, D38). Read live, so a comment shows as soon as it
 * is posted. `truncated` means the ticket held more than the server reads for
 * one report.
 */
export async function getBugReportComments(id: string) {
  return get<{ success: boolean; data: { comments: BugReportComment[]; truncated: boolean } }>(
    `/bug-reports/${encodeURIComponent(id)}/comments`
  );
}

/**
 * crypto.randomUUID() only exists in a secure context, and this app is served
 * over plain http in local dev. The fallback does not need to be
 * cryptographically strong — it is a collision guard for one user's retried
 * submit, and the server is what actually enforces it (the unique submissionId
 * index for a report, the conditional push for a comment).
 */
export function newSubmissionId(): string {
  const c = window.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `sub-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

type ReportAnswer = { success: boolean; data: { report: BugReportSummary } };

/**
 * BR-1: add a comment to the report's USC discussion. `clientId` is one per draft
 * (newSubmissionId): re-sending it after a failure or a double click lands on the
 * same single comment. Answers with the whole report, discussion included.
 */
export async function addBugReportComment(id: string, clientId: string, body: string) {
  return postJson<{ success: boolean; data: { report: BugReportSummary; duplicate: boolean } }>(
    `/bug-reports/${encodeURIComponent(id)}/comments`,
    { clientId, body }
  );
}

/** BR-1: edit your own comment. */
export async function editBugReportComment(id: string, commentId: string, body: string) {
  return patch<ReportAnswer>(
    `/bug-reports/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
    { body }
  );
}

/** BR-1: delete your own comment. Deleting one already gone succeeds. */
export async function deleteBugReportComment(id: string, commentId: string) {
  return deleteRequest<ReportAnswer>(
    `/bug-reports/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`
  );
}

/** D47: one report the picker offers. `linkedToTicket`: picking it links to that original instead. */
export type BugLinkCandidate = {
  id: string;
  ticketNumber: string;
  title: string;
  status: BugStatus;
  createdAt: string;
  pageLabel: string;
  isMine: boolean;
  linkedToTicket: string | null;
};

/**
 * D47: the reports that can be picked as "the same problem". With no search, the
 * open reports filed from `screen`, newest first; with one, every report you may
 * see whose title or ticket number matches, Fixed included. `forId` is the report
 * being linked, which is never offered, nor are the reports already linked to it.
 */
export async function listLinkCandidates(params: { forId?: string; screen?: string; q?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.forId) qs.set('for', params.forId);
  if (params.screen) qs.set('screen', params.screen);
  if (params.q?.trim()) qs.set('q', params.q.trim());
  return get<{ success: boolean; data: { candidates: BugLinkCandidate[]; limit: number } }>(
    `/bug-reports/link-candidates?${qs.toString()}`
  );
}

type LinkAnswer = { success: boolean; data: { report: BugReportSummary; unchanged: boolean } };

/**
 * D47: mark report `id` as the same problem as `targetId`. It lands on the original,
 * and any reports linked to `id` move along with it, so reload the list afterwards.
 */
export async function linkBugReport(id: string, targetId: string) {
  return postJson<LinkAnswer>(`/bug-reports/${encodeURIComponent(id)}/link`, { targetId });
}

/** D47: unlink it again; it goes back to its own status. */
export async function unlinkBugReport(id: string) {
  return deleteRequest<LinkAnswer>(`/bug-reports/${encodeURIComponent(id)}/link`);
}

export async function getUnseenStatusCount() {
  return get<{ success: boolean; data: { count: number } }>('/bug-reports/unseen-count');
}

export async function markReportsSeen() {
  return postJson<{ success: boolean; data: { updated: number } }>('/bug-reports/mark-seen', {});
}

/**
 * Mint a short-lived signed URL for a report's screenshot and return it absolute,
 * ready for an <img src>. The server returns a root-relative path; an <img> on a
 * split FE/BE deployment needs the API origin in front of it.
 */
export async function getScreenshotUrl(id: string): Promise<string> {
  const res = await get<{ success: boolean; data: { url: string; expiresAt: string } }>(
    `/bug-reports/${encodeURIComponent(id)}/screenshot-url`
  );
  return `${getApiBaseUrl()}${res.data.url}`;
}
